import {
    ArrowBack as ArrowBackIcon,
    CheckCircle as CheckIcon,
    LocalOffer as OfferIcon,
    Public as PublicIcon,
    School as SchoolIcon,
    AccessTime as TimeIcon
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    Chip,
    CircularProgress,
    Container,
    Divider,
    Paper,
    Stack,
    Typography
} from '@mui/material';
import Grid from '@mui/material/Grid';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

const CourseDetails = () => {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const location = useLocation();
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const apiURL = process.env.REACT_APP_API;

    // Get course ID from URL
    const getCourseId = () => {
        const params = new URLSearchParams(location.search);
        return params.get('courseid');
    };

    useEffect(() => {
        fetchCourseDetails();
    }, []);

    const fetchCourseDetails = async () => {
        const courseId = getCourseId();
        if (!courseId) {
            toast.error('No course ID provided');
            navigate('/courses');
            return;
        }

        setLoading(true);
        try {
            const response = await axios.get(`${apiURL}/public/course/details`, {
                params: { courseid: courseId }
            });

            if (response.data.status === 'success' || response.data.status === true) {
                setCourse(response.data.data);
            } else {
                toast.error(response.data.message || 'Failed to fetch course details');
                //navigate('/courses');
            }
        } catch (error) {
            console.error('Error fetching course details:', error);
            toast.error(error.response?.data?.message || 'An error occurred while fetching course details');
            //navigate('/courses');
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        navigate(-1);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount);
    };

    const calculateDiscount = (maxPrice, price) => {
        return Math.round(((maxPrice - price) / maxPrice) * 100);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'active': return 'success';
            case 'pending': return 'warning';
            case 'archive': return 'error';
            default: return 'default';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'active': return 'Active';
            case 'pending': return 'Pending';
            case 'archive': return 'Archived';
            default: return status;
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <Stack spacing={2} alignItems="center">
                    <CircularProgress size={48} />
                    <Typography variant="body1" color="text.secondary">
                        Loading course details...
                    </Typography>
                </Stack>
            </Box>
        );
    }

    if (!course) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <Alert severity="error" sx={{ maxWidth: 500 }}>
                    Course not found. Please check the URL and try again.
                </Alert>
            </Box>
        );
    }

    const discount = calculateDiscount(course.c_max_price, course.c_price);

    return (
        <Box sx={{ py: 4 }}>
            <Container maxWidth="lg">
                {/* Back Button */}
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                    sx={{ mb: 3 }}
                >
                    Back to Courses
                </Button>

                {/* Main Course Card */}
                <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', mb: 4 }}>
                    {/* Header with Icon */}
                    <Box sx={{
                        bgcolor: 'primary.main',
                        color: 'white',
                        p: 4,
                        position: 'relative'
                    }}>
                        <Stack direction="row" alignItems="flex-start" spacing={3}  >
                            <Box sx={{
                                bgcolor: 'white !important',
                                borderRadius: 2,
                                p: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <SchoolIcon sx={{ fontSize: 40 }} color='secondary' />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                                    <Typography variant="h4" fontWeight={700} color='secondary'>
                                        {course.c_title}
                                    </Typography>
                                    <Chip
                                        label={getStatusLabel(course.c_status)}
                                        color={getStatusColor(course.c_status)}
                                        size="small"
                                    />
                                </Stack>
                                <Typography variant="body2" mt={-0.5} sx={{ opacity: 0.9 }} color='secondary'>
                                    {course.c_name}
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>

                    {/* Course Details */}
                    <Box sx={{ p: 4 }}>
                        <Grid container spacing={4}>
                            {/* Left Column - Course Info */}
                            <Grid item xs={12} md={8}>
                                {/* Description */}
                                <Box sx={{ mb: 4 }}>
                                    <Typography variant="h6" gutterBottom fontWeight={600}>
                                        Course Description
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary" paragraph>
                                        {course.c_description}
                                    </Typography>
                                </Box>

                                <Divider sx={{ my: 3 }} />

                                {/* Website Content from react-quill */}
                                <Box sx={{ mb: 4 }}>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                        <PublicIcon color="primary" />
                                        <Typography variant="h6" fontWeight={600}>
                                            Course Content
                                        </Typography>
                                    </Stack>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 3,
                                            bgcolor: 'grey.50',
                                            borderRadius: 2,
                                            border: '1px solid',
                                            borderColor: 'divider'
                                        }}
                                    >
                                        {/* Display react-quill content */}
                                        <div
                                            dangerouslySetInnerHTML={{ __html: course.website_content || '<p>No content available.</p>' }}
                                            style={{
                                                fontFamily: 'inherit',
                                                fontSize: '1rem',
                                                lineHeight: 1.6,
                                                color: 'text.primary'
                                            }}
                                        />
                                        {!course.website_content && (
                                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                Course content will be available soon.
                                            </Typography>
                                        )}
                                    </Paper>
                                </Box>
                            </Grid>

                            {/* Right Column - Pricing & Actions */}
                            <Grid item xs={12} md={4}>
                                <Card variant="outlined" sx={{ borderRadius: 2, p: 3 }}>
                                    {/* Duration */}
                                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                                        <Box sx={{
                                            bgcolor: 'primary.lighter',
                                            borderRadius: 1,
                                            p: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <TimeIcon color="primary" />
                                        </Box>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">
                                                Course Duration
                                            </Typography>
                                            <Typography variant="h6" fontWeight={600}>
                                                {course.c_duration} months
                                            </Typography>
                                        </Box>
                                    </Stack>

                                    <Divider sx={{ my: 3 }} />

                                    {/* Pricing */}
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="h6" gutterBottom fontWeight={600}>
                                            Course Fee
                                        </Typography>

                                        {/* Discount Display */}
                                        {discount > 0 && (
                                            <Chip
                                                label={`${discount}% OFF`}
                                                color="success"
                                                icon={<OfferIcon />}
                                                sx={{ mb: 2, fontWeight: 600 }}
                                            />
                                        )}

                                        {/* Pricing Details */}
                                        <Stack spacing={1.5}>
                                            {course.c_price < course.c_max_price && (
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Original Price:
                                                    </Typography>
                                                    <Typography
                                                        variant="body1"
                                                        sx={{
                                                            textDecoration: 'line-through',
                                                            color: 'text.secondary'
                                                        }}
                                                    >
                                                        {formatCurrency(course.c_max_price)}
                                                    </Typography>
                                                </Stack>
                                            )}

                                            <Stack direction="row" alignItems="baseline" spacing={1}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Discounted Price:
                                                </Typography>
                                                <Typography variant="h4" fontWeight={700} color="primary.main">
                                                    {formatCurrency(course.c_price)}
                                                </Typography>
                                            </Stack>

                                            {discount > 0 && (
                                                <Typography variant="caption" color="success.main">
                                                    You save {formatCurrency(course.c_max_price - course.c_price)}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </Box>

                                    <Divider sx={{ my: 3 }} />

                                    {/* Features */}
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                            What's Included:
                                        </Typography>
                                        <Stack spacing={1.5}>
                                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                                <CheckIcon fontSize="small" color="success" />
                                                <Typography variant="body2">
                                                    Certificate of Completion
                                                </Typography>
                                            </Stack>
                                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                                <CheckIcon fontSize="small" color="success" />
                                                <Typography variant="body2">
                                                    Lifetime Access
                                                </Typography>
                                            </Stack>
                                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                                <CheckIcon fontSize="small" color="success" />
                                                <Typography variant="body2">
                                                    24/7 Support
                                                </Typography>
                                            </Stack>
                                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                                <CheckIcon fontSize="small" color="success" />
                                                <Typography variant="body2">
                                                    Project-based Learning
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </Box>

                                    {/* Enroll Button */}
                                    <NavLink to={`/courses/enquiry?courseid=${course.courseid}`}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            size="large"
                                            disabled={course.c_status !== 'active'}
                                            sx={{
                                                py: 1.5,
                                                borderRadius: 2,
                                                fontWeight: 600,
                                                fontSize: '1rem'
                                            }}
                                        >
                                            {course.c_status === 'active' ? 'Enroll Now' : 'Course Not Available'}
                                        </Button>
                                    </NavLink>

                                    {/* Status Message */}
                                    {course.c_status !== 'active' && (
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block', textAlign: 'center' }}>
                                            This course is currently {getStatusLabel(course.c_status).toLowerCase()}
                                        </Typography>
                                    )}
                                </Card>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>

                {/* Additional Info */}
                <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                            Need help deciding? Contact our support team
                        </Typography>
                        <Button variant="outlined" size="small" href='/contact' >
                            Contact Support
                        </Button>
                    </Stack>
                </Paper>
            </Container>
        </Box>
    );
};

export default CourseDetails;